/**
 * usePermissions · composable reactive para permisos granulares (Fase 3).
 *
 * Lee feature_permissions del current_account_user via store auth.
 * Default legacy mode = full access (no rompe agentes/admins existentes).
 *
 * Uso:
 *   const { can, canSpecial, isLockedToBranch, accessibleBranchIds } = usePermissions();
 *   if (!can('chat.view')) hide();
 *   if (canSpecial('move_lead_between_stages')) enableDrag();
 */
import { computed } from 'vue';
import { useStore } from 'vuex';

export function usePermissions() {
  const store = useStore();

  // currentAccount es la entrada en currentUser.accounts del account activo.
  // Tiene: role, permissions (legacy), feature_permissions, accessible_branch_ids
  const currentAccount = computed(() => store.getters.getCurrentAccount || {});
  const featurePermissions = computed(() => currentAccount.value?.feature_permissions || {});
  const role = computed(() => currentAccount.value?.role || 'agent');

  // Lista de sucursales accesibles. Vacío = todas (super admin).
  const accessibleBranchIds = computed(() => currentAccount.value?.accessible_branch_ids || []);
  // Super admin (escape hatch · bypass total para evitar lockout)
  const isSuperAdmin = computed(() => currentAccount.value?.super_admin === true);

  // Admin o Super Admin · gate de acciones destructivas/admin (regla 2026-05-19,
  // reemplaza "solo super admin" 2026-05-12). Agente normal → false.
  // OJO: la sucursal personal/privada queda EXCLUIDA aparte (solo dueño/super).
  const isAdminOrSuperAdmin = computed(
    () => isSuperAdmin.value || role.value === 'administrator'
  );

  // True si tiene UNA sola sucursal asignada → switcher se bloquea.
  // EXCEPCIÓN: super_admin nunca se bloquea (bypass total)
  const isLockedToBranch = computed(() => {
    if (isSuperAdmin.value) return false;
    return accessibleBranchIds.value.length === 1;
  });
  const lockedBranchId = computed(() => isLockedToBranch.value ? accessibleBranchIds.value[0] : null);

  // True si el agente puede acceder a la sucursal dada.
  // EXCEPCIÓN: super_admin siempre puede acceder a cualquier sucursal
  function canAccessBranch(branchId) {
    if (isSuperAdmin.value) return true;
    if (accessibleBranchIds.value.length === 0) return true; // sin restricción
    return accessibleBranchIds.value.includes(branchId);
  }

  // Modo activo del feature_permissions: 'legacy' | 'custom'.
  const permissionsMode = computed(() => {
    const fp = featurePermissions.value;
    return fp && fp._mode === 'custom' ? 'custom' : 'legacy';
  });

  // Check principal · permission_key formato "modulo.accion" o nombre de special action.
  // Reglas (fix estricto):
  //   - super_admin → true (escape hatch · evita lockout · espejo de AccountUser#can?)
  //   - feature_permissions vacío o _mode != custom → true (legacy mode)
  //   - _mode == custom → solo true si la clave está marcada true en modules{} o en top-level (specials)
  function can(permissionKey) {
    if (isSuperAdmin.value) return true;
    const fp = featurePermissions.value;
    if (!fp || permissionsMode.value !== 'custom') return true;

    const key = String(permissionKey);
    if (key.includes('.')) {
      return (fp.modules || {})[key] === true;
    }
    // Special action (top-level boolean)
    return fp[key] === true;
  }

  // Alias semántico para acciones especiales.
  function canSpecial(actionKey) { return can(actionKey); }

  // ¿Puede ver al menos UNA acción dentro de un módulo (para mostrar/ocultar item del menú)?
  function canAnyOnModule(moduleKey) {
    if (isSuperAdmin.value) return true;
    if (permissionsMode.value !== 'custom') return true;
    const fp = featurePermissions.value;
    const modules = fp?.modules || {};
    return Object.keys(modules).some(k => k.startsWith(`${moduleKey}.`) && modules[k] === true);
  }

  return {
    can,
    canSpecial,
    canAnyOnModule,
    canAccessBranch,
    accessibleBranchIds,
    isLockedToBranch,
    lockedBranchId,
    permissionsMode,
    role,
    isSuperAdmin,
    isAdminOrSuperAdmin,
  };
}
